import React from 'react';
import PropTypes from 'prop-types';
import { InstantSearch, Configure } from 'react-instantsearch-dom';
import qs from 'query-string';
import ReactTooltip from 'react-tooltip';

import IconClose from 'components/icons/iconClose';
import CustomerNameWithEmoji from 'components/consumer/CustomerNameWithEmoji';
import MobileModal from 'components/consumer/MobileModal';
import IconMobileGrid1x from 'components/icons/iconMobileGrid1x';
import IconMobileGrid2x from 'components/icons/iconMobileGrid2x';
import IconMobileGrid3x from 'components/icons/iconMobileGrid3x';
import ShopByTitleNavigation from 'components/consumer/ShopByTitleNavigation';
import ClearAllFiltersButton from 'components/consumer/ClearAllFiltersButton';
import GridWithSidebar from 'components/consumer/GridWithSidebar';
import MobileFiltersButton from 'components/consumer/MobileFiltersButton';
import HotspotProductCard from 'components/consumer/hotspot-wrapper/HotspotProductCard';
import browserStorage from 'components/consumer/browser-storage';
import scrollTo from 'commons/scrollTo';
import { ALGOLIA_INDEX_NAME } from 'commons/constants';
import CtaButton from 'components/essentials/Cta/CtaButton';
import { noEmptySearchClient } from 'components/essentials/AlgoliaSearchClient';
import ShopByMobileFiltersBar from 'components/consumer/ShopByMobileFiltersBar';
import MobileOnly from 'components/consumer/MobileOnly';
import TabletAndDesktopOnly from 'components/consumer/TabletAndDesktopOnly';
import withScreen from 'components/hoc/withScreen';
import connectInfiniteHits from '../connect-infinite-hits';
import {
  PhotoCardsResult,
  InspirationSearchBox,
  InspirationSearchOptions,
  transformInspirationImageUrl,
} from '../search-state/utils';
import InspirationFiltersTop from '../inspiration-filters/InspirationFiltersTop';

class InspirationPage extends React.Component {
  pageRef = React.createRef();

  componentDidUpdate(prevProps) {
    if (
      !this.props.isMobile &&
      prevProps.hits &&
      !prevProps.hits.length &&
      this.props.hits &&
      this.props.hits.length
    ) {
      const prevScrollPosition = browserStorage().getItem(
        'scrollPosition.customerPhotosPage'
      );
      if (prevScrollPosition) {
        setTimeout(() => {
          scrollTo({
            top: parseInt(prevScrollPosition, 10),
            behavior: 'smooth',
          });
          browserStorage().setItem('scrollPosition.customerPhotosPage', 0);
        }, 0);
      }
    }
  }

  renderFilters = isOnMobile => (
    <InspirationFiltersTop
      isMobile={isOnMobile}
      facets={this.props.facets}
      filterActions={this.props.filterActions}
      filterState={this.props.filterState}
    />
  );

  renderInspirationPage = () => (
    <div
      // We use a custom className here to support overriding nested third
      // party styles in globals-tailwind.css
      className="inspiration-page [&_.infinite-scroll-component]:!m-0 md:ml-[3.333vw] relative"
      ref={this.pageRef}
    >
      <GridWithSidebar
        getCustomProps={this.getCustomProps}
        gridItemType="customer-photo"
        mobileItemsPerRow={this.props.mobileItemsPerRow}
        naturalHeightImages
        renderGrid={this.renderGrid}
        tabsConfig={{
          products: true,
          productsTitle: ' ',
          productsHideLoadMore: true,
        }}
        variant="InspirationPage"
      />

      <TabletAndDesktopOnly>
        <ReactTooltip
          // Note: The hotspot-tooltip-wrapper class is required for the
          // HotspotRenderer component to target this correctly (since it is a
          // third party we cannot use a data attribute here)
          className="max-[767px]:hidden hotspot-tooltip-wrapper p-0 !absolute !opacity-100 [transition:all_.3s_ease-in-out] !text-gray-light8 !bg-gray-light8 hover:!text-[#eaeaeb] hover:![background:#eaeaeb]"
          type="light"
          effect="solid"
          event="none"
          scrollHide={false}
          clickable
          overridePosition={({ left, top }) => {
            const rootRef = this.pageRef.current;
            const position = rootRef.getBoundingClientRect();
            return { left: left - position.left, top: top - position.top };
          }}
          getContent={product_data => {
            if (!product_data) return null;
            const productDataSplits = product_data.split('?');
            const productId = parseInt(productDataSplits[0], 10);
            if (!productId) return null;
            const productConfig = qs.parse(productDataSplits[1] || '');
            return (
              <div
                className="w-[150px]"
                key={`hotspot-tooltip-${productId}-${productDataSplits[1] ||
                  ''}`}
              >
                <HotspotProductCard
                  productId={productId}
                  productConfig={productConfig}
                />
              </div>
            );
          }}
        />
      </TabletAndDesktopOnly>
    </div>
  );

  renderGrid = ({
    selectedGridItem,
    updateSelectedGridItem,
    showOnTop = false,
  }) => (
    <PhotoCardsResult
      filterActions={this.props.filterActions}
      hasFilters={this.props.filterState.isFiltered}
      hasMore={this.props.hasMore}
      hits={this.props.hits}
      loadMore={this.props.onLoadMoreResults}
      mobileItemsPerRow={this.props.mobileItemsPerRow}
      refine={this.props.refine}
      searchPhotoId={this.props.filterState.photoId}
      selectedGridItem={selectedGridItem}
      showOnTop={showOnTop}
      updateSelectedGridItem={updateSelectedGridItem}
      variant="InspirationPage"
    />
  );

  getCustomProps = selectedGridItem => {
    if (!selectedGridItem) {
      return {};
    }

    const customerName = `${
      selectedGridItem.customer_first_name
    } ${selectedGridItem.customer_last_name &&
      selectedGridItem.customer_last_name[0]}.`;

    const products = selectedGridItem.products || [];
    return {
      imageProps: {
        src: transformInspirationImageUrl(selectedGridItem.image_link),
        trim: 'auto',
      },
      selectedItemName: (
        <CustomerNameWithEmoji>{customerName}</CustomerNameWithEmoji>
      ),
      selectedItemNameMobile: (
        <span className="CustomerPhotosCustomerNameMobile">{customerName}</span>
      ),
      selectedItemProducts: products,
      selectedItemMaterialList: products.map(p => p.upholstery),
      selectedItemDescription: selectedGridItem.photo_comment || '',
      showDescriptionQuoteMark: true,
      onProductCardClick: () => {
        if (!this.props.isMobile) {
          if (selectedGridItem) {
            browserStorage().setItem(
              'selectedCustomerPhotoItem',
              JSON.stringify(selectedGridItem)
            );
            // store filter state as well
            const tempFilters = {};
            if (this.props.filterState.roomFilter !== '') {
              tempFilters.roomFilter = this.props.filterState.roomFilter;
            }
            if (this.props.filterState.materialFilter !== '') {
              tempFilters.materialFilter = this.props.filterState.materialFilter;
            }
            if (
              this.props.filterState.colorFilters &&
              Object.keys(this.props.filterState.colorFilters).length > 0
            ) {
              tempFilters.colorFilters = {
                ...this.props.filterState.colorFilters,
              };
            }
            browserStorage().setItem(
              'customerPhotosPage.filters',
              JSON.stringify(tempFilters)
            );
          } else {
            browserStorage().removeItem('selectedCustomerPhotoItem');
            browserStorage().removeItem('scrollPosition.customerPhotosPage');
            browserStorage().removeItem('customerPhotosPage.filters');
          }
        }
      },
    };
  };

  getNumberOfActiveFilters = () => {
    if (!this.props.filterState) {
      return 0;
    }

    const {
      materialFilter = '',
      colorFilters = {},
      roomFilter = '',
    } = this.props.filterState;

    const materialFilterCount = materialFilter ? 1 : 0;
    const colorFilterCount = Object.keys(colorFilters).length;
    const roomFilterCount = roomFilter ? 1 : 0;

    return materialFilterCount + colorFilterCount + roomFilterCount;
  };

  render() {
    const numberOfActiveFilters = this.getNumberOfActiveFilters();

    const {
      filterState,
      filterActions,
      onSearchUpdate,
      mobileItemsPerRow,
      resetProductSearch,
      productSearchQuery,
      productSearchState,
      toggleMobileItemsPerRow,
      toggleProductSearchResults,
    } = this.props;

    const mobileModalTitle =
      numberOfActiveFilters > 0
        ? `Filters (${numberOfActiveFilters})`
        : 'Filters';

    const mobileModalHeader = !!filterState.isFiltered && (
      <div className="-ml-4 -mr-4 pl-4 pr-4 -mb-2.5 md:bg-white md:h-10 md:pb-3">
        <ClearAllFiltersButton
          onClick={filterActions.onClearFilters}
          variant="ShopByMobile"
        >
          Clear all
        </ClearAllFiltersButton>
      </div>
    );

    const mobileModalFooter = (
      <CtaButton block onClick={filterActions.closeMobileFilters}>
        Done ({this.props.hits ? this.props.hits.length : 0} result
        {(this.props.hits ? this.props.hits.length : 0) === 1 ? '' : 's'})
      </CtaButton>
    );

    const mobileModalContent = <>{this.renderFilters(true)}</>;

    return (
      <>
        <MobileOnly>
          <div className="md:hidden">
            <MobileModal
              title={mobileModalTitle}
              onClose={filterActions.closeMobileFilters}
              isOpen={!!filterState.isMobileFiltersOpen}
              isFullHeight
              headerRight={mobileModalHeader}
              stickyFooter={mobileModalFooter}
              className="MobileFiltersModal"
              closeIcon="arrow"
            >
              {mobileModalContent}
            </MobileModal>
          </div>
        </MobileOnly>

        <div className="customer-photos__container relative">
          <MobileOnly>
            <div className="md:hidden">
              <ShopByTitleNavigation page="Inspiration" />
            </div>

            <ShopByMobileFiltersBar className="gap-4">
              <MobileFiltersButton
                onClick={filterActions.openMobileFilters}
                numberOfActiveFilters={numberOfActiveFilters}
                noMargin
              />

              <div className="relative">
                <InstantSearch
                  searchClient={noEmptySearchClient}
                  searchState={productSearchState}
                  onSearchStateChange={onSearchUpdate}
                  indexName={ALGOLIA_INDEX_NAME.PRODUCTS}
                >
                  <Configure hitsPerPage="24" query={productSearchQuery} />
                  <InspirationSearchBox
                    resetProductSearch={resetProductSearch}
                    onProductClicked={filterActions.onProductIdFilterChange}
                  />
                  <InspirationSearchOptions
                    fabricsData={this.props.fabricsData}
                    onSearchUpdate={onSearchUpdate}
                    onMaterialOptionChange={
                      filterActions.onMaterialOptionChange
                    }
                    onProductClicked={filterActions.onProductIdFilterChange}
                    isProductIdFiltered={filterState.isProductIdFiltered}
                    isMaterialOptionFiltered={
                      filterState.isMaterialOptionFiltered
                    }
                    toggleProductSearchResults={toggleProductSearchResults}
                  />
                </InstantSearch>
              </div>

              <span className="items-center flex flex-1 justify-end">
                <button
                  className="bg-none items-center bg-white border-0 flex justify-center ml-[3px] focus-visible:outline-offset-4"
                  onClick={toggleMobileItemsPerRow}
                  type="button"
                >
                  {mobileItemsPerRow === 1 ? (
                    <IconMobileGrid1x />
                  ) : mobileItemsPerRow === 2 ? (
                    <IconMobileGrid2x />
                  ) : (
                    <IconMobileGrid3x />
                  )}
                </button>
              </span>
            </ShopByMobileFiltersBar>

            <div className="md:hidden">{this.renderInspirationPage()}</div>
          </MobileOnly>

          <TabletAndDesktopOnly>
            <div className="max-[767px]:hidden">
              <ShopByTitleNavigation page="Inspiration">
                <div className="relative">
                  <InstantSearch
                    searchClient={noEmptySearchClient}
                    searchState={productSearchState}
                    onSearchStateChange={onSearchUpdate}
                    indexName={ALGOLIA_INDEX_NAME.PRODUCTS}
                  >
                    <Configure hitsPerPage="24" query={productSearchQuery} />
                    <InspirationSearchBox
                      resetProductSearch={resetProductSearch}
                      onProductClicked={filterActions.onProductIdFilterChange}
                    />
                    <InspirationSearchOptions
                      fabricsData={this.props.fabricsData}
                      onSearchUpdate={onSearchUpdate}
                      onMaterialOptionChange={
                        filterActions.onMaterialOptionChange
                      }
                      onProductClicked={filterActions.onProductIdFilterChange}
                      isProductIdFiltered={filterState.isProductIdFiltered}
                      isMaterialOptionFiltered={
                        filterState.isMaterialOptionFiltered
                      }
                      toggleProductSearchResults={toggleProductSearchResults}
                    />
                  </InstantSearch>
                </div>
              </ShopByTitleNavigation>
            </div>

            <div className="max-[767px]:hidden pl-[3.333vw] pr-[3.333vw] [border-bottom:1px_solid_#f1f1f1]">
              <div className="flex-wrap pt-4 px-0 pb-0 md:items-start md:flex md:min-h-[100px] md:mt-0 md:mb-0">
                {this.renderFilters(false)}
              </div>
              {filterState.selectedFiltersList.length > 0 && (
                <div className="flex flex-wrap">
                  {filterState.selectedFiltersList.map(filter => (
                    <button
                      type="button"
                      className="text-sm font-bold rounded-[30px] mt-1 mr-4 mb-4 ml-0 pt-[.4rem] px-4 pb-2 bg-brand border border-solid border-brand capitalize text-white [&_svg]:w-2 [&_svg]:cursor-pointer [&_svg_path]:fill-white flex items-center space-x-2"
                      key={filter.name}
                      onClick={() => {
                        filter.onDeselect(filter.filterName, filter.name);
                        if (filter.name === 'product') {
                          resetProductSearch();
                        }
                      }}
                    >
                      <span>{filter.filterName}</span>
                      <IconClose />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="max-[767px]:hidden">
              {this.renderInspirationPage()}
            </div>
          </TabletAndDesktopOnly>
        </div>
      </>
    );
  }
}

InspirationPage.propTypes = {
  filterState: PropTypes.shape({
    roomFilter: PropTypes.string,
    materialFilter: PropTypes.string,
    colorFilters: PropTypes.object,
    isFiltered: PropTypes.bool,
    isMobileFiltersOpen: PropTypes.bool,
    isProductIdFiltered: PropTypes.bool,
    isMaterialOptionFiltered: PropTypes.bool,
    photoId: PropTypes.string,
    selectedFiltersList: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        filterName: PropTypes.string,
        onDeselect: PropTypes.func,
      })
    ),
  }),
  filterActions: PropTypes.shape({
    onClearFilters: PropTypes.func,
    onMaterialOptionChange: PropTypes.func,
    onProductIdFilterChange: PropTypes.func,
    openMobileFilters: PropTypes.func,
    closeMobileFilters: PropTypes.func,
    toggleMobileItemsPerRow: PropTypes.func,
    toggleProductSearchResults: PropTypes.func,
  }),
  onSearchUpdate: PropTypes.func,
  mobileItemsPerRow: PropTypes.number,
  resetProductSearch: PropTypes.func,
  productSearchQuery: PropTypes.string,
  productSearchState: PropTypes.shape({
    query: PropTypes.string,
    page: PropTypes.number,
    hitsPerPage: PropTypes.number,
    filters: PropTypes.arrayOf(PropTypes.string),
  }),
  toggleMobileItemsPerRow: PropTypes.func,
  toggleProductSearchResults: PropTypes.func,
  fabricsData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      id: PropTypes.string,
    })
  ),
  hits: PropTypes.array,
  isFiltered: PropTypes.bool,
  isMobile: PropTypes.bool,
  isMobileFiltersOpen: PropTypes.bool,
  isProductIdFiltered: PropTypes.bool,
  isMaterialOptionFiltered: PropTypes.bool,
  onProductIdFilterChange: PropTypes.func,
  onMaterialOptionChange: PropTypes.func,
  onClearFilters: PropTypes.func,
  facets: PropTypes.array,
  refine: PropTypes.func,
  hasMore: PropTypes.bool,
  onLoadMoreResults: PropTypes.func,
};

export default withScreen(connectInfiniteHits(InspirationPage));
